// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 62.57.0 Slice E (D51, D67) — whether the processing-service
 * certificate register is offered to this organisation.
 *
 * One read of `GET /api/v1/service-certificates/availability` per signed-in
 * session, held in a module-level cache that every mounted instance shares, and
 * read again only through `refresh` / `refreshServiceCertificateAvailability`
 * (the Organization profile calls it after a statement is saved, so the
 * sidebar leaf follows the statement without a reload).
 *
 * - **Not read without the grant.** A viewer without `service_certificate:read`
 *   cannot open the register; nothing is read and `offered` stays `false`.
 * - **Failure hides the leaf and is logged, never shown.** A failed read
 *   leaves `availability` `null`, so `offered` is `false` (D67). A failed
 *   refresh keeps the last good answer.
 * - **Signing out forgets the answer.** The cache is forgotten when the last
 *   consumer unmounts, the `useSectorOverlayNavigation` precedent.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { Permission, type ServiceCertificateAvailability } from '@vn-grc/shared';
import { apiClient } from '../services/api-client.js';
import { useAuth } from '../contexts/AuthContext.js';
import { usePermission } from './usePermission.js';
import { userRecordIdOf } from '../utils/user-record-id.js';

interface Snapshot {
  availability: ServiceCertificateAvailability | null;
  loading: boolean;
  failed: boolean;
}

const EMPTY: Snapshot = { availability: null, loading: false, failed: false };

let snapshot: Snapshot = EMPTY;
let cacheKey: string | null = null;
let requested = false;
let generation = 0;
const listeners = new Set<() => void>();

function publish(next: Snapshot): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

/** Forget the answer and drop any read in flight. */
export function forgetServiceCertificateAvailability(): void {
  generation += 1;
  requested = false;
  cacheKey = null;
  publish(EMPTY);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) forgetServiceCertificateAvailability();
  };
}

async function read(): Promise<void> {
  const mine = ++generation;
  publish({ ...snapshot, loading: true });
  try {
    const availability = await apiClient.getServiceCertificateAvailability();
    if (mine !== generation) return;
    publish({ availability, loading: false, failed: false });
  } catch (err) {
    if (mine !== generation) return;
    // D67 — the leaf is hidden and the failure logged; the last good answer stays.
    console.warn('[service-certificates] availability read failed', err);
    publish({ availability: snapshot.availability, loading: false, failed: true });
  }
}

/**
 * Read the availability again and notify every mounted instance (after a
 * profile statement is saved). Does nothing before the first read was asked.
 */
export async function refreshServiceCertificateAvailability(): Promise<void> {
  if (!requested) return;
  await read();
}

export interface ServiceCertificateAvailabilityResult {
  availability: ServiceCertificateAvailability | null;
  /** D51 `offered`; `false` while loading, after a failed first read and without the grant. */
  offered: boolean;
  loading: boolean;
  failed: boolean;
  refresh: () => Promise<void>;
}

export function useServiceCertificateAvailability(): ServiceCertificateAvailabilityResult {
  const { user } = useAuth();
  const canRead = usePermission(Permission.SERVICE_CERTIFICATE_READ);
  const current = useSyncExternalStore(subscribe, () => snapshot);
  const key = user ? `${user.org_id ?? ''}:${userRecordIdOf(user) ?? ''}` : null;

  useEffect(() => {
    if (!key) {
      if (cacheKey !== null) forgetServiceCertificateAvailability();
      return;
    }
    if (!canRead) return;
    if (cacheKey !== key) {
      forgetServiceCertificateAvailability();
      cacheKey = key;
    }
    if (requested) return;
    requested = true;
    void read();
  }, [key, canRead]);

  const refresh = useCallback(async () => {
    requested = true;
    await read();
  }, []);

  const availability = canRead ? current.availability : null;
  return {
    availability,
    offered: availability?.offered === true,
    loading: canRead && current.loading,
    failed: canRead && current.failed,
    refresh,
  };
}
